import React, { ReactNode, RefObject } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import { TuneOutlined } from '@mui/icons-material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RefreshIcon from '@mui/icons-material/Refresh'
import SavedSearchIcon from '@mui/icons-material/SavedSearch'
import { Box, Button, IconButton, Tooltip } from '@mui/material'
import { useScreenDimensions } from '@shared/hooks/useScreenDimensions'

import { SavedSearchBtn, SearchCountBadge } from './index.styles'

interface SearchButtonsProps {
  onResetAll: () => void
  hasAccessToSavedSearch: boolean
  ref: RefObject<HTMLButtonElement | null>
  isSavedSearchButtonEnabled: boolean
  appliedSavedSearchName?: string
  handleSavedSearchesOverviewClick: (e: React.MouseEvent<HTMLElement>) => void
  savedSearchesBtnTitle: ReactNode
  newSavedSearchesCount: number
  badgeContent: ReactNode
  toggleFiltersDrawer?: () => void
}

export const SearchButtons = ({
  onResetAll,
  hasAccessToSavedSearch,
  ref,
  isSavedSearchButtonEnabled,
  appliedSavedSearchName,
  handleSavedSearchesOverviewClick,
  savedSearchesBtnTitle,
  newSavedSearchesCount,
  badgeContent,
  toggleFiltersDrawer,
}: SearchButtonsProps) => {
  const { t } = useTranslation()
  const { isSmallMobile } = useScreenDimensions()

  return (
    <Box display="flex" alignItems="center" gap="10px">
      {toggleFiltersDrawer &&
        (isSmallMobile ? (
          <Tooltip title="filters">
            <IconButton id="search-filters_button" onClick={toggleFiltersDrawer} data-testid="filters_button" size="small">
              <TuneOutlined color="primary" />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            id="search-filters_button"
            onClick={toggleFiltersDrawer}
            data-testid="filters_button"
            size="small"
            startIcon={<TuneOutlined />}
          >
            {t(translationKeys.SEARCH_FILTERS_TITLE)}
          </Button>
        ))}

      {isSmallMobile ? (
        <IconButton id="search-reset_all" onClick={onResetAll} data-testid="reset_all_btn" size="small">
          <RefreshIcon color="primary" />
        </IconButton>
      ) : (
        <Button
          id="search-reset_all"
          onClick={onResetAll}
          data-testid="reset_all_btn"
          size="large"
          variant="contained"
          sx={{ flex: '0 0 auto' }}
        >
          {t(translationKeys.SEARCH_RESET_SEARCH_BUTTON)}
        </Button>
      )}

      {hasAccessToSavedSearch &&
        (isSmallMobile ? (
          <IconButton
            id="saved_searches-button"
            ref={ref}
            onClick={handleSavedSearchesOverviewClick}
            disabled={!isSavedSearchButtonEnabled}
            data-testid="saved_searches-button"
            size="small"
          >
            <SearchCountBadge overlap="circular" color="secondary" badgeContent={badgeContent} right="0" top="0">
              <SavedSearchIcon color="primary" />
            </SearchCountBadge>
          </IconButton>
        ) : (
          <SavedSearchBtn
            id="saved_searches-button"
            ref={ref}
            disabled={!isSavedSearchButtonEnabled}
            $saveSearchActive={!!appliedSavedSearchName}
            endIcon={<ExpandMoreIcon color={appliedSavedSearchName ? 'primary' : undefined} />}
            size="medium"
            variant="outlined"
            onClick={handleSavedSearchesOverviewClick}
            sx={{ flex: '0 0 auto' }}
          >
            {savedSearchesBtnTitle}
            {newSavedSearchesCount > 0 && <SearchCountBadge overlap="circular" color="secondary" badgeContent={badgeContent} />}
          </SavedSearchBtn>
        ))}
    </Box>
  )
}
